import { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Upload, X, Loader2, ChevronDown, ChevronUp, Package, Image as ImageIcon, Tag, DollarSign, Layers, FileText, Percent, Sparkles, Pencil } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import ImageEditor from './ImageEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AddProductPage({ store, language, isDarkMode, onBack, onSuccess, categories = [], editItem = null, isEditMode = false }) {
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [aiDescBusy, setAiDescBusy] = useState(false); // iter-301 — AI smart description generator
  const [sections, setSections] = useState({ basic: true, pricing: true, inventory: false, options: false });
  const [images, setImages] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  
  // Initialize form with editItem data if in edit mode
  const [form, setForm] = useState(() => {
    if (editItem && isEditMode) {
      return {
        name: editItem.name || '',
        nameEn: editItem.nameEn || '',
        description: editItem.description || '',
        price: editItem.price || '',
        salePrice: editItem.salePrice || '',
        quantity: editItem.quantity || '',
        sku: editItem.sku || '',
        category_id: editItem.category_id || '',
        subcategory_id: editItem.subcategory_id || '',
        status: editItem.status || 'active',
        trackInventory: editItem.trackInventory !== false,
        weight: editItem.weight || '',
        dimensions: editItem.dimensions || '',
        colors: editItem.colors || [],
        sizes: editItem.sizes || []
      };
    }
    return {
      name: '', nameEn: '', description: '', price: '', salePrice: '', quantity: '',
      sku: '', category_id: '', subcategory_id: '', status: 'active', trackInventory: true,
      weight: '', dimensions: '', colors: [], sizes: []
    };
  });

  // Initialize images from editItem if in edit mode
  useEffect(() => {
    if (editItem && isEditMode) {
      const existingImages = editItem.images || (editItem.image ? [editItem.image] : []);
      if (existingImages.length > 0) {
        setImages(existingImages.map(url => ({ preview: url, isExisting: true })));
      }
    }
  }, [editItem, isEditMode]);

  const toggle = (s) => setSections(p => ({ ...p, [s]: !p[s] }));
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // iter-292 — Live preview of the customer-facing final price (base + commission + admin fee).
  // Fetches commission settings once (session-protected). Never shown to customers.
  const [commission, setCommission] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('business_token');
    if (!token) return;
    axios
      .get(`${BACKEND_URL}/api/merchant/commission-settings`, { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 })
      .then((r) => setCommission(r.data))
      .catch(() => { /* silent fallback: no preview row */ });
  }, []);

  const computeFinal = (base) => {
    const n = parseFloat(base);
    if (isNaN(n) || n < 0) return null;
    // iter-297 — even when commission is still loading we show the "you keep X" row
    // so the merchant isn't left staring at an empty box after typing their price.
    const pct = commission?.commission_enabled ? Math.max(0, Math.min(100, commission.commission_percent || 0)) : 0;
    const commissionAmt = Math.round((n * pct) / 100 * 100) / 100;
    const adminFee = commission?.admin_fee_enabled ? Math.max(0, commission.admin_fee_amount || 0) : 0;
    const final = Math.round((n + commissionAmt + adminFee) * 100) / 100;
    return { base: n, commissionAmt, adminFee, pct, final, anyEnabled: pct > 0 || adminFee > 0, loaded: !!commission };
  };
  const preview = computeFinal(form.price);

  // iter-296 — Image editor state. Opens whenever a new image is picked.
  const [editorPending, setEditorPending] = useState(null); // { file, preview } awaiting edit

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 10) { toast.error(language === 'ar' ? 'الحد الأقصى 10 صور' : 'Max 10 images'); return; }
    // Only pass the FIRST file through the editor so we don't open 5 editors in a row.
    // Any extra files are appended as-is (user can edit them one-by-one from the
    // "تعديل" icon on each thumbnail below).
    const [first, ...rest] = files;
    if (first) {
      const reader = new FileReader();
      reader.onload = (ev) => setEditorPending({ file: first, preview: ev.target.result });
      reader.readAsDataURL(first);
    }
    rest.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => setImages((prev) => [...prev, { file, preview: ev.target.result }]);
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  // Called when the merchant clicks the pencil on any existing thumbnail.
  const [editingIndex, setEditingIndex] = useState(null);
  const openEditorForExisting = async (idx) => {
    const img = images[idx];
    if (!img) return;
    try {
      // Normalize into {file, preview} so the editor can work the same way.
      let file = img.file;
      if (!file) {
        const r = await fetch(img.preview, { mode: 'cors' });
        const blob = await r.blob();
        file = new File([blob], `existing-${idx}.png`, { type: blob.type || 'image/png' });
      }
      setEditingIndex(idx);
      setEditorPending({ file, preview: img.preview });
    } catch {
      toast.error(language === 'ar' ? 'تعذّر فتح المحرر' : 'Could not open editor');
    }
  };

  const handleEditorConfirm = ({ file, preview }) => {
    if (editingIndex !== null) {
      // Replace in place — keeps `isExisting` flag off so the new file uploads.
      setImages((prev) => prev.map((img, i) => (i === editingIndex ? { file, preview } : img)));
    } else {
      setImages((prev) => [...prev, { file, preview }]);
    }
    setEditorPending(null);
    setEditingIndex(null);
  };

  const removeImage = (idx) => {
    setImages(p => p.filter((_, i) => i !== idx));
    if (mainImageIndex === idx) setMainImageIndex(0);
    else if (mainImageIndex > idx) setMainImageIndex(p => p - 1);
  };

  // iter-293/296 — AI product image analyzer (CGB-003). Now works for BOTH
  // freshly uploaded images AND images already on the server (edit mode).
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const analyzeMainImage = async () => {
    const mainImg = images[mainImageIndex] || images[0];
    if (!mainImg) {
      toast.error(language === 'ar' ? 'أضف صورة أولاً' : 'Add an image first');
      return;
    }
    setAiAnalyzing(true);
    try {
      const token = localStorage.getItem('business_token');
      let file = mainImg.file;
      // Edit mode: image came from the server as a URL, not a File.
      // Pull it down so we can POST it.
      if (!file && mainImg.preview) {
        const r = await fetch(mainImg.preview, { mode: 'cors' });
        const blob = await r.blob();
        file = new File([blob], 'existing.png', { type: blob.type || 'image/png' });
      }
      if (!file) { setAiAnalyzing(false); return; }
      const fd = new FormData();
      fd.append('image', file);
      toast.info(language === 'ar' ? '🔍 جارٍ تحليل الصورة...' : '🔍 Analyzing image...');
      const res = await axios.post(`${BACKEND_URL}/api/ai/analyze-product-image`, fd, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000,
      });
      if (res.data?.success) {
        const d = res.data;
        setForm((p) => ({
          ...p,
          name: p.name || d.product_name || '',
          nameEn: p.nameEn || d.product_name_en || '',
          description: p.description || d.product_description || '',
        }));
        toast.success(language === 'ar' ? '✨ تم ملء بيانات المنتج من الصورة' : '✨ Product fields auto-filled from image');
      } else {
        toast.error(language === 'ar' ? 'لم يتمكن الذكاء الاصطناعي من قراءة الصورة' : 'AI could not read the image');
      }
    } catch {
      toast.error(language === 'ar' ? 'فشل تحليل الصورة' : 'Image analysis failed');
    } finally {
      setAiAnalyzing(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name) { toast.error(language === 'ar' ? 'يرجى إدخال اسم المنتج' : 'Please enter product name'); return; }
    if (!form.price) { toast.error(language === 'ar' ? 'يرجى إدخال السعر' : 'Please enter price'); return; }
    // iter-293 — images are now OPTIONAL for ecommerce (user feedback:
    // some merchants want to list a product first and add images later).
    // We keep the warning as a toast but allow the save to proceed.

    try {
      setSaving(true);
      const token = localStorage.getItem('business_token');
      const data = new FormData();
      
      if (!isEditMode) {
        data.append('storeType', 'ecommerce');
      }
      
      // Append form fields
      Object.keys(form).forEach(k => {
        if (k === 'colors' || k === 'sizes') {
          data.append(k, JSON.stringify(form[k]));
        } else if (form[k] !== '' && form[k] !== null && form[k] !== undefined) {
          data.append(k, form[k]);
        }
      });
      
      // Handle existing images (URLs) vs new images (files)
      const existingUrls = [];
      let newImageIndex = 0;
      images.forEach((img, idx) => {
        if (img.isExisting) {
          existingUrls.push(img.preview);
        } else if (img.file) {
          // Send actual File object, not base64 preview
          if (idx === mainImageIndex) {
            data.append('mainImage', img.file, img.file.name);
          } else {
            data.append(`extraImage${newImageIndex + 1}`, img.file, img.file.name);
            newImageIndex++;
          }
        }
      });
      
      if (existingUrls.length > 0) {
        data.append('existingImages', JSON.stringify(existingUrls));
      }
      
      if (isEditMode && editItem) {
        // PUT request for update
        await axios.put(`${BACKEND_URL}/api/stores/${store.id}/items/${editItem.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(language === 'ar' ? 'تم تحديث المنتج بنجاح' : 'Product updated successfully');
      } else {
        // POST request for create
        await axios.post(`${BACKEND_URL}/api/stores/${store.id}/items`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(language === 'ar' ? 'تم إضافة المنتج بنجاح' : 'Product added successfully');
      }
      
      onSuccess?.();
      onBack?.();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(language === 'ar' ? 'حدث خطأ أثناء الحفظ' : 'Error saving product');
    } finally {
      setSaving(false);
    }
  };

  const cls = isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : 'bg-white border-gray-200';
  const labelCls = `block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  // Get selected category for subcategories
  const selectedCategory = categories.find(c => c.id === form.category_id);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'} shadow-sm`}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-[#1E9BD9]">
            <ArrowLeft className="h-5 w-5" />
            <span>{language === 'ar' ? 'رجوع' : 'Back'}</span>
          </button>
          <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
            {isEditMode 
              ? (language === 'ar' ? 'تعديل المنتج' : 'Edit Product')
              : (language === 'ar' ? 'إضافة منتج جديد' : 'Add New Product')
            }
          </h1>
          <div className="w-20" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4 pb-24">
        {/* Images Section */}
        <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              <ImageIcon className="h-5 w-5 text-[#1E9BD9]" />
              {language === 'ar' ? 'صور المنتج' : 'Product Images'} ({images.length}/10)
            </h3>
          </div>
          
          <div className="grid grid-cols-5 gap-3">
            {images.map((img, idx) => (
              <div key={idx} className={`relative aspect-square rounded-xl overflow-hidden border-2 group ${idx === mainImageIndex ? 'border-[#1E9BD9]' : 'border-transparent'}`}>
                <img src={img.preview} alt="" className="w-full h-full object-cover" />
                <button onClick={() => removeImage(idx)} className="absolute top-1 right-1 p-1 bg-red-500 rounded-full text-white" data-testid={`remove-image-${idx}`}><X className="h-3 w-3" /></button>
                {/* iter-296 — pencil to re-open the editor on an existing image */}
                <button
                  onClick={() => openEditorForExisting(idx)}
                  className="absolute top-1 left-1 p-1 bg-blue-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  title={language === 'ar' ? 'تعديل الصورة' : 'Edit image'}
                  data-testid={`edit-image-${idx}`}
                >
                  <Pencil className="h-3 w-3" />
                </button>
                {idx !== mainImageIndex && (
                  <button onClick={() => setMainImageIndex(idx)} className="absolute bottom-1 left-1 text-[10px] px-1.5 py-0.5 bg-black/50 rounded text-white">
                    {language === 'ar' ? 'رئيسية' : 'Main'}
                  </button>
                )}
                {idx === mainImageIndex && <div className="absolute bottom-1 left-1 text-[10px] px-1.5 py-0.5 bg-[#1E9BD9] rounded text-white">{language === 'ar' ? 'الرئيسية' : 'Main'}</div>}
              </div>
            ))}
            {images.length < 10 && (
              <button onClick={() => fileInputRef.current?.click()} className={`aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center ${isDarkMode ? 'border-white/20 hover:border-[#1E9BD9]' : 'border-gray-300 hover:border-[#1E9BD9]'}`} data-testid="add-image-btn">
                <Upload className="h-6 w-6 text-[#1E9BD9]" />
                <span className="text-xs mt-1 text-gray-500">{language === 'ar' ? 'إضافة' : 'Add'}</span>
              </button>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />

          {/* iter-293/296 — AI analyze (CGB-003 bot). Visible whenever ANY
              image is present (works in edit-mode too — we fetch the server
              URL and send it). */}
          {images.length > 0 && (
            <button
              type="button"
              onClick={analyzeMainImage}
              disabled={aiAnalyzing}
              className={`mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white transition-all active:scale-[0.98] ${
                aiAnalyzing
                  ? 'bg-gradient-to-r from-[#1E9BD9]/70 via-[#0D7AB5]/70 to-[#065A82]/70 cursor-wait'
                  : 'bg-gradient-to-r from-[#1E9BD9] via-[#0D7AB5] to-[#065A82] hover:opacity-95 shadow-lg'
              }`}
              data-testid="ai-analyze-btn"
            >
              {aiAnalyzing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {language === 'ar' ? 'جارٍ التحليل…' : 'Analyzing…'}
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  {language === 'ar' ? 'تحليل الصورة واستخراج مواصفات المنتج' : 'Analyze image & extract product specs'}
                </>
              )}
            </button>
          )}
        </div>

        {/* Basic Info Section */}
        <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-200'}`}>
          <button onClick={() => toggle('basic')} className={`w-full p-4 flex items-center justify-between ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
            <h3 className={`font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              <Package className="h-5 w-5 text-[#1E9BD9]" />
              {language === 'ar' ? 'المعلومات الأساسية' : 'Basic Information'}
            </h3>
            {sections.basic ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </button>
          {sections.basic && (
            <div className="p-4 pt-0 space-y-4">
              {/* Product Name */}
              <div>
                <label className={labelCls}>{language === 'ar' ? 'اسم المنتج *' : 'Product Name *'}</label>
                <Input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder={language === 'ar' ? 'مثال: هاتف آيفون 15' : 'e.g., iPhone 15'} className={cls} />
              </div>
              
              {/* Product Name English */}
              <div>
                <label className={labelCls}>{language === 'ar' ? 'اسم المنتج بالإنجليزية' : 'Product Name (English)'}</label>
                <Input value={form.nameEn} onChange={(e) => update('nameEn', e.target.value)} placeholder="e.g., iPhone 15" className={cls} />
              </div>

              {/* Category */}
              {categories.length > 0 && (
                <div className="space-y-4">
                  {/* Main Category */}
                  <div>
                    <label className={labelCls}>{language === 'ar' ? 'الفئة الرئيسية' : 'Main Category'}</label>
                    <select value={form.category_id} onChange={(e) => update('category_id', e.target.value)} className={`w-full px-3 py-2 border rounded-xl ${cls}`}>
                      <option value="">{language === 'ar' ? 'اختر الفئة...' : 'Select category...'}</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{language === 'ar' ? (cat.name_ar || cat.name) : (cat.name_en || cat.nameEn)}</option>
                      ))}
                    </select>
                  </div>
                  {/* Sub Category */}
                  <div>
                    <label className={labelCls}>{language === 'ar' ? 'الفئة الفرعية' : 'Sub Category'}</label>
                    <select value={form.subcategory_id} onChange={(e) => update('subcategory_id', e.target.value)} disabled={!form.category_id} className={`w-full px-3 py-2 border rounded-xl ${cls}`}>
                      <option value="">{language === 'ar' ? 'اختر الفئة الفرعية...' : 'Select subcategory...'}</option>
                      {selectedCategory?.subcategories?.map((sub) => (
                        <option key={sub.id} value={sub.id}>{language === 'ar' ? (sub.name_ar || sub.name) : (sub.name_en || sub.nameEn)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Description */}
              <div>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <label className={labelCls}>{language === 'ar' ? 'وصف المنتج' : 'Product Description'}</label>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!form.name?.trim()) {
                        toast.error(language === 'ar' ? 'أدخل اسم المنتج أولاً' : 'Enter the product name first');
                        return;
                      }
                      try {
                        setAiDescBusy(true);
                        const token = localStorage.getItem('business_token');
                        const { data } = await axios.post(
                          `${BACKEND_URL}/api/ai/product/generate-description`,
                          {
                            name: form.name,
                            category: form.subcategory || form.category || '',
                            price: form.price || undefined,
                            keywords: (form.tags || '').split(',').map(s => s.trim()).filter(Boolean),
                          },
                          { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 }
                        );
                        const txt = (language === 'ar' ? data?.description : (data?.description_en || data?.description)) || '';
                        if (txt) {
                          update('description', txt);
                          toast.success(language === 'ar' ? '✨ تم توليد الوصف' : '✨ Description generated');
                        } else {
                          toast.error(language === 'ar' ? 'لم يُرجع المساعد وصفاً' : 'Assistant returned no text');
                        }
                      } catch (err) {
                        const detail = err?.response?.data?.detail || err?.message || 'failed';
                        toast.error((language === 'ar' ? 'فشل التوليد: ' : 'Generation failed: ') + String(detail).slice(0, 120));
                      } finally {
                        setAiDescBusy(false);
                      }
                    }}
                    disabled={aiDescBusy}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${
                      aiDescBusy
                        ? 'opacity-60 cursor-wait'
                        : isDarkMode
                          ? 'bg-amber-500/15 text-amber-300 border-amber-400/30 hover:bg-amber-500/25'
                          : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                    }`}
                    data-testid="ai-generate-description-btn"
                  >
                    {aiDescBusy ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {language === 'ar' ? 'جارٍ التوليد…' : 'Generating…'}
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-3 w-3" />
                        {language === 'ar' ? '✨ اقترح وصفاً ذكياً' : '✨ Smart description'}
                      </>
                    )}
                  </button>
                </div>
                <textarea value={form.description} onChange={(e) => update('description', e.target.value)} rows={4} placeholder={language === 'ar' ? 'أضف وصفاً تفصيلياً للمنتج...' : 'Add detailed product description...'} className={`w-full px-3 py-2 border rounded-xl resize-none ${cls}`} />
              </div>
            </div>
          )}
        </div>

        {/* Pricing Section */}
        <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-200'}`}>
          <button onClick={() => toggle('pricing')} className={`w-full p-4 flex items-center justify-between ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
            <h3 className={`font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              <DollarSign className="h-5 w-5 text-green-500" />
              {language === 'ar' ? 'التسعير' : 'Pricing'}
            </h3>
            {sections.pricing ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </button>
          {sections.pricing && (
            <div className="p-4 pt-0 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{language === 'ar' ? 'السعر (درهم) *' : 'Price (AED) *'}</label>
                  <Input type="number" inputMode="decimal" value={form.price} onChange={(e) => update('price', e.target.value)} placeholder="0.00" className={cls} />
                </div>
                <div>
                  <label className={labelCls}>{language === 'ar' ? 'سعر التخفيض' : 'Sale Price'}</label>
                  <Input type="number" inputMode="decimal" value={form.salePrice} onChange={(e) => update('salePrice', e.target.value)} placeholder="0.00" className={cls} />
                </div>
              </div>
              {form.salePrice && form.price && parseFloat(form.salePrice) < parseFloat(form.price) && (
                <div className="flex items-center gap-2 p-3 bg-green-500/10 rounded-xl">
                  <Percent className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600">
                    {language === 'ar' ? 'خصم' : 'Discount'}: {Math.round((1 - parseFloat(form.salePrice) / parseFloat(form.price)) * 100)}%
                  </span>
                </div>
              )}

              {/* iter-297 — Live customer-price preview. Always visible as soon as
                  the merchant types a price (>0) so the "customer will see X" math
                  is unmistakable. When commission + admin-fee are both OFF we
                  show a neutral "you keep the full price" row instead. */}
              {preview && preview.base > 0 && (
                <div
                  className={`rounded-xl p-4 border ${isDarkMode ? 'bg-gradient-to-br from-emerald-500/10 via-blue-500/10 to-amber-500/10 border-emerald-400/30' : 'bg-gradient-to-br from-emerald-50 via-blue-50 to-amber-50 border-emerald-200'}`}
                  data-testid="price-preview"
                >
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <p className={`text-sm font-bold flex items-center gap-1.5 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                      <DollarSign className="h-4 w-4" />
                      {language === 'ar' ? 'كم سيدفع العميل؟' : 'What the customer pays'}
                    </p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700'}`} data-testid="price-preview-visibility">
                      {language === 'ar' ? 'مرئي لك فقط' : 'For you only'}
                    </span>
                  </div>
                  <div className={`space-y-1.5 text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    <div className="flex justify-between items-center">
                      <span className="opacity-80">{language === 'ar' ? 'سعرك (ما ستستلمه)' : 'Your price (you receive)'}</span>
                      <span className="font-bold text-emerald-600" data-testid="price-preview-base">
                        {preview.base.toFixed(2)} {language === 'ar' ? 'د.إ' : 'AED'}
                      </span>
                    </div>
                    {preview.pct > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="opacity-80">+ {language === 'ar' ? `عمولة المنصة ${preview.pct}%` : `Platform commission ${preview.pct}%`}</span>
                        <span className="font-semibold text-blue-600" data-testid="price-preview-commission">
                          {preview.commissionAmt.toFixed(2)} {language === 'ar' ? 'د.إ' : 'AED'}
                        </span>
                      </div>
                    )}
                    {preview.adminFee > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="opacity-80">+ {language === 'ar' ? 'رسوم إدارية' : 'Admin fee'}</span>
                        <span className="font-semibold text-amber-600" data-testid="price-preview-fee">
                          {preview.adminFee.toFixed(2)} {language === 'ar' ? 'د.إ' : 'AED'}
                        </span>
                      </div>
                    )}
                    <div className={`flex justify-between items-center pt-2 mt-2 border-t ${isDarkMode ? 'border-white/20' : 'border-emerald-200'}`}>
                      <span className="font-bold">{language === 'ar' ? 'السعر النهائي للعميل' : 'Final price (customer)'}</span>
                      <span className="font-extrabold text-xl text-emerald-600" data-testid="price-preview-final">
                        {preview.final.toFixed(2)} {language === 'ar' ? 'د.إ' : 'AED'}
                      </span>
                    </div>
                  </div>
                  {preview.anyEnabled ? (
                    <p className={`mt-3 text-[11px] leading-snug ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {language === 'ar'
                        ? '💡 العمولة والرسوم تُضاف فوق سعرك — ليست من ربحك. مبيعاتك دائماً تُحسب بسعرك الأصلي.'
                        : '💡 Commission + fees are added on top of your price — not deducted from it. Your sales are always counted at your base price.'}
                    </p>
                  ) : (
                    <p className={`mt-3 text-[11px] leading-snug ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                      {language === 'ar'
                        ? '✓ لا توجد عمولة أو رسوم حالياً — ستستلم كامل السعر.'
                        : '✓ No commission or fees currently — you receive the full price.'}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Inventory Section */}
        <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-200'}`}>
          <button onClick={() => toggle('inventory')} className={`w-full p-4 flex items-center justify-between ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
            <h3 className={`font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              <Layers className="h-5 w-5 text-purple-500" />
              {language === 'ar' ? 'المخزون' : 'Inventory'}
            </h3>
            {sections.inventory ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </button>
          {sections.inventory && (
            <div className="p-4 pt-0 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{language === 'ar' ? 'الكمية المتوفرة' : 'Stock Quantity'}</label>
                  <Input type="number" inputMode="decimal" value={form.quantity} onChange={(e) => update('quantity', e.target.value)} placeholder="0" className={cls} />
                </div>
                <div>
                  <label className={labelCls}>{language === 'ar' ? 'رمز المنتج (SKU)' : 'SKU'}</label>
                  <Input value={form.sku} onChange={(e) => update('sku', e.target.value)} placeholder="SKU-001" className={cls} />
                </div>
              </div>
              
              {/* Status */}
              <div>
                <label className={labelCls}>{language === 'ar' ? 'الحالة' : 'Status'}</label>
                <select value={form.status} onChange={(e) => update('status', e.target.value)} className={`w-full px-3 py-2 border rounded-xl ${cls}`}>
                  <option value="active">{language === 'ar' ? 'متوفر' : 'In Stock'}</option>
                  <option value="out_of_stock">{language === 'ar' ? 'نفذت الكمية' : 'Out of Stock'}</option>
                  <option value="hidden">{language === 'ar' ? 'مخفي' : 'Hidden'}</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Options Section */}
        <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-200'}`}>
          <button onClick={() => toggle('options')} className={`w-full p-4 flex items-center justify-between ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
            <h3 className={`font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              <Tag className="h-5 w-5 text-orange-500" />
              {language === 'ar' ? 'الخيارات والمواصفات' : 'Options & Specs'}
            </h3>
            {sections.options ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
          </button>
          {sections.options && (
            <div className="p-4 pt-0 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{language === 'ar' ? 'الوزن (كجم)' : 'Weight (kg)'}</label>
                  <Input type="number" inputMode="decimal" value={form.weight} onChange={(e) => update('weight', e.target.value)} placeholder="0.5" className={cls} />
                </div>
                <div>
                  <label className={labelCls}>{language === 'ar' ? 'الأبعاد (سم)' : 'Dimensions (cm)'}</label>
                  <Input value={form.dimensions} onChange={(e) => update('dimensions', e.target.value)} placeholder="20x10x5" className={cls} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Submit — inline at end of form */}
        <Button onClick={handleSubmit} disabled={saving} className="w-full h-12 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90 text-white rounded-xl text-base font-bold disabled:opacity-50" data-testid="add-product-submit-btn">
          {saving 
            ? <><Loader2 className="h-5 w-5 animate-spin me-2" />{language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}</>
            : isEditMode 
              ? (language === 'ar' ? 'حفظ التعديلات' : 'Save Changes')
              : (language === 'ar' ? 'إضافة المنتج' : 'Add Product')
          }
        </Button>
      </div>

      {/* iter-296 — full-screen image editor */}
      {editorPending && (
        <ImageEditor
          sourceImage={editorPending}
          language={language}
          isDarkMode={isDarkMode}
          onCancel={() => { setEditorPending(null); setEditingIndex(null); }}
          onConfirm={handleEditorConfirm}
        />
      )}
    </div>
  );
}
